import { useNavigate } from 'react-router-dom'
import { useEffect } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, Button, Text } from '@flatfile/baseline'
import { App } from '@flatfile/api-internal/api'
import getCollection from '../resources/support/getCollection'
import { useCollection } from '../resources/hooks/useCollection'

interface DeleteAppModalProps {
  app: App
  opened: boolean
  onClose: () => void
  onDelete: (appId: string) => Promise<void>
}

export const DeleteAppModal = ({ app, opened, onClose, onDelete }: DeleteAppModalProps) => {
  const spaces = useCollection('SPACES', { pageSize: 1000, filterByEnvId: false, app: app.id, isAppTemplate: false })
  const envCount = getCollection('ENVIRONMENTS').getItems().length
  const nav = useNavigate()

  useEffect(() => {
    getCollection('SPACES', { pageSize: 1000, filterByEnvId: false, app: app.id }).refresh()
  }, [opened])

  return (
    <Dialog open={opened} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{spaces.length === 0 ? 'Proceed with deleting?' : 'Cannot delete app with spaces'}</DialogTitle>
        </DialogHeader>
        <div className='flex flex-col gap-4'>
          {spaces.length === 0 && (
            <Text>
              You are about to permanently delete the{' '}
              <Text as='span' emphasized>
                {app.name}
              </Text>{' '}
              App. This action cannot be reversed.
            </Text>
          )}

          {spaces.length > 0 && (
            <Text>
              You have{' '}
              <Text as='span' emphasized>
                {spaces.length > 1000 ? 'over 1000' : `${spaces.length}`}
              </Text>{' '}
              {spaces.length > 1 ? app.entityPlural : app.entity} {envCount === 1 ? 'in' : 'across'}{' '}
              <Text as='span' emphasized>
                {envCount}
              </Text>{' '}
              environment{envCount === 1 ? '' : 's'} in this app.
              <Text emphasized>You have to move or delete spaces before deleting this app.</Text>
            </Text>
          )}

          <div className='flex flex-col gap-2 mt-2'>
            {spaces.length === 0 ? (
              <Button variant='destructive' className='w-full' onClick={() => onDelete(app.id)}>
                Yes, Delete {app.name} App Permanently
              </Button>
            ) : (
              <Button
                variant='secondary'
                className='w-full'
                onClick={() => nav(`/apps/${app?.type === 'CUSTOM' ? `custom/${app.id}` : app.namespace}/list/recent/`)}
              >
                Take me to {app.name} App to manage {spaces.length > 1 ? app.entityPlural : app.entity}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
